/**
 * Zod schemas for the Norfolk Southern consist request integration served
 * by the etl_integrations digital dispatch framework.
 *
 * A consist request is fetched from Norfolk Southern at request time and
 * stored whole on the request row: there are no partner status updates, so
 * `consistState` is terminal from the moment the row exists. Field casing is
 * camelCase, mirroring the backend `NorfolkSouthernTransformedRequest`.
 */
import { z } from 'zod';
import {
  DIGITAL_DISPATCH_INTEGRATIONS,
  DigitalDispatchQueueStateSchema,
} from './digitalDispatchSchemas';

const TrainPositionSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});
export type NorfolkSouthernTrainPosition = z.infer<typeof TrainPositionSchema>;

const TrainSnapshotFields = {
  externalId: z.string().min(1),
  trainId: z.string().min(1),
  trainSymbol: z.string().nullish(),
  position: TrainPositionSchema,
  speedMph: z.number().int().nullish(),
  headingDegrees: z.number().int().nullish(),
  numberOfCars: z.number().int().nullish(),
  trainLengthFeet: z.number().int().nullish(),
  status: z.string().nullish(),
};

/**
 * Body for POST /api/norfolk-southern/requests. `externalId` is the dynamic
 * object the user picked on the map, so a request always starts from a
 * train the map knows about rather than a typed in ID.
 */
export const NorfolkSouthernConsistRequestSchema = z.object({
  ...TrainSnapshotFields,
  /** The date the train departed its origin, as YYYY-MM-DD. */
  originDate: z.string().min(1),
  reason: z.string().min(3).max(500),
  /** Whether the train belongs to another railroad; sent to NS as isForeign. */
  foreignTrain: z.boolean(),
  callerId: z.string().nullish(),
});
export type NorfolkSouthernConsistRequest = z.infer<
  typeof NorfolkSouthernConsistRequestSchema
>;

const TrainSnapshotSchema = z.object({
  ...TrainSnapshotFields,
  originDate: z.string().nullish(),
});
export type NorfolkSouthernTrainSnapshot = z.infer<typeof TrainSnapshotSchema>;

const ConsistHazmatSourceSchema = z.object({
  ergGuideNumber: z.string().nullish(),
  ergUrl: z.string(),
  hazardClass: z.string().nullish(),
  unNaId: z.string().nullish(),
  packingGroup: z.string().nullish(),
  properShippingName: z.string().nullish(),
  technicalName: z.string().nullish(),
  inhalationHazard: z.string().nullish(),
  emergencyContactName: z.string().nullish(),
  emergencyContactNumber: z.string().nullish(),
});
export type ConsistHazmatSource = z.infer<typeof ConsistHazmatSourceSchema>;

const ConsistContainerSchema = z.object({
  containerId: z.string(),
  commodity: z.string().nullish(),
  hazmatSources: z.array(ConsistHazmatSourceSchema).default([]),
});
export type ConsistContainer = z.infer<typeof ConsistContainerSchema>;

const ConsistCarSchema = z.object({
  sequence: z.number().int().nullish(),
  reportingMarks: z.string(),
  carType: z.string().nullish(),
  loaded: z.boolean(),
  commodity: z.string().nullish(),
  quantity: z.number().int().nullish(),
  hazmat: z.boolean(),
  hazmatSources: z.array(ConsistHazmatSourceSchema).default([]),
  containers: z.array(ConsistContainerSchema).default([]),
});
export type ConsistCar = z.infer<typeof ConsistCarSchema>;

const ConsistTrainSchema = z.object({
  trainId: z.string().nullish(),
  numberOfCars: z.number().int().nullish(),
  numberOfLoads: z.number().int().nullish(),
  numberOfEmpties: z.number().int().nullish(),
  numberOfHazmatCars: z.number().int().nullish(),
  lengthFeet: z.number().int().nullish(),
  speedMph: z.number().int().nullish(),
  origin: z.string().nullish(),
  destination: z.string().nullish(),
  travelDirection: z.string().nullish(),
  lastReportedPosition: TrainPositionSchema.nullish(),
  reportedAt: z.string().nullish(),
});
export type ConsistTrain = z.infer<typeof ConsistTrainSchema>;

const ConsistSchema = z.object({
  train: ConsistTrainSchema,
  cars: z.array(ConsistCarSchema).default([]),
});
export type Consist = z.infer<typeof ConsistSchema>;

const ConsistRequesterSchema = z.object({
  id: z.number(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});
export type ConsistRequester = z.infer<typeof ConsistRequesterSchema>;

export const CONSIST_STATES = ['completed', 'failed'] as const;
const ConsistStateSchema = z.enum(CONSIST_STATES);
export type ConsistState = z.infer<typeof ConsistStateSchema>;

/** The `request` field of a Norfolk Southern digital dispatch row. */
export const NorfolkSouthernTransformedRequestSchema = z.object({
  train: TrainSnapshotSchema,
  phoneNumber: z.string(),
  reason: z.string(),
  requester: ConsistRequesterSchema,
  requestedAt: z.string(),
  consistState: ConsistStateSchema,
  consist: ConsistSchema.nullish(),
  error: z.string().nullish(),
});
export type NorfolkSouthernTransformedRequest = z.infer<
  typeof NorfolkSouthernTransformedRequestSchema
>;

// Norfolk Southern sends no status stream, so the framework's status list is
// always empty for these rows. The schema is kept open so a future status
// shape cannot break list parsing.
const NorfolkSouthernStatusResponseSchema = z.object({
  id: z.number(),
  requestId: z.number(),
  createdAt: z.string(),
  integration: z.literal(DIGITAL_DISPATCH_INTEGRATIONS.NORFOLK_SOUTHERN),
  status: z.record(z.unknown()),
});

export const NorfolkSouthernRequestResponseSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  callerId: z.string().nullish(),
  integration: z.literal(DIGITAL_DISPATCH_INTEGRATIONS.NORFOLK_SOUTHERN),
  queueState: DigitalDispatchQueueStateSchema,
  request: NorfolkSouthernTransformedRequestSchema,
  statuses: z.array(NorfolkSouthernStatusResponseSchema).default([]),
});
export type NorfolkSouthernRequestResponse = z.infer<
  typeof NorfolkSouthernRequestResponseSchema
>;

const DigitalDispatchListItemProbeSchema = z
  .object({ integration: z.string() })
  .passthrough();

/**
 * Picks the Norfolk Southern rows out of GET /api/digital-dispatch/requests,
 * which returns every integration's requests for the account. Rows from
 * other integrations are skipped. A malformed Norfolk Southern row is
 * skipped too, with an error logged, so one bad row cannot blank the queue.
 */
export function parseNorfolkSouthernRequests(
  response: unknown,
): NorfolkSouthernRequestResponse[] {
  const items = z.array(z.unknown()).parse(response);

  const requests: NorfolkSouthernRequestResponse[] = [];
  for (const item of items) {
    const probe = DigitalDispatchListItemProbeSchema.safeParse(item);
    if (
      !probe.success ||
      probe.data.integration !== DIGITAL_DISPATCH_INTEGRATIONS.NORFOLK_SOUTHERN
    ) {
      continue;
    }
    const parsed = NorfolkSouthernRequestResponseSchema.safeParse(item);
    if (!parsed.success) {
      // eslint-disable-next-line no-console -- schema mismatch is a bug to see in sentry, but the rest of the queue must still render
      console.error(
        'parseNorfolkSouthernRequests: skipping malformed row',
        (item as { id?: unknown }).id,
        parsed.error,
      );
      continue;
    }
    requests.push(parsed.data);
  }

  return requests;
}
